import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatCard } from "@/components/ui/stat-card";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, TrendingUp, TrendingDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { AlmasaLoading } from "@/components/brand/AlmasaLoading";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { useIsMobile } from "@/hooks/use-mobile";
import { RentabilidadCardMobile } from "@/components/rentabilidad/RentabilidadCardMobile";

interface ProductoRentabilidad {
  id: string;
  codigo: string;
  nombre: string;
  marca: string | null;
  precio_compra: number;
  precio_venta: number;
  margen_pesos: number;
  margen_porcentaje: number;
  stock_actual: number;
  valor_inventario: number;
}

const Rentabilidad = () => {
  const [productos, setProductos] = useState<ProductoRentabilidad[]>([]);
  const [filteredProductos, setFilteredProductos] = useState<ProductoRentabilidad[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [ordenamiento, setOrdenamiento] = useState("margen_desc");
  const isMobile = useIsMobile();

  useEffect(() => {
    loadProductos();
  }, []);

  useEffect(() => {
    filterAndSort();
  }, [productos, searchTerm, ordenamiento]);

  const loadProductos = async () => {
    try {
      const { data, error } = await supabase
        .from("productos")
        .select("id, codigo, nombre, marca, precio_compra, precio_venta, stock_actual, costo_promedio_ponderado, ultimo_costo_compra")
        .eq("activo", true)
        .gt("precio_venta", 0)
        .order("nombre");

      if (error) throw error;

      const productosConMargen: ProductoRentabilidad[] = (data || [])
        .filter((p) => (p.costo_promedio_ponderado || p.ultimo_costo_compra || p.precio_compra) > 0)
        .map((p) => {
        const costoRef = p.costo_promedio_ponderado || p.ultimo_costo_compra || p.precio_compra;
        const margen_pesos = p.precio_venta - costoRef;
        const margen_porcentaje = (margen_pesos / p.precio_venta) * 100;
        const valor_inventario = p.stock_actual * costoRef;

        return {
          id: p.id,
          codigo: p.codigo,
          nombre: p.nombre,
          marca: p.marca,
          precio_compra: costoRef,
          precio_venta: p.precio_venta,
          margen_pesos,
          margen_porcentaje,
          stock_actual: p.stock_actual,
          valor_inventario,
        };
      });

      setProductos(productosConMargen);
    } catch (error) {
      console.error("Error cargando productos:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSort = () => {
    let filtered = productos.filter(
      (p) =>
        p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.marca && p.marca.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Ordenar
    switch (ordenamiento) {
      case "margen_desc":
        filtered.sort((a, b) => b.margen_porcentaje - a.margen_porcentaje);
        break;
      case "margen_asc":
        filtered.sort((a, b) => a.margen_porcentaje - b.margen_porcentaje);
        break;
      case "venta_desc":
        filtered.sort((a, b) => b.precio_venta - a.precio_venta);
        break;
      case "valor_inv_desc":
        filtered.sort((a, b) => b.valor_inventario - a.valor_inventario);
        break;
      default:
        break;
    }

    setFilteredProductos(filtered);
  };

  const getMargenBadge = (porcentaje: number) => {
    if (porcentaje < 10) {
      return <Badge variant="destructive">Bajo ({porcentaje.toFixed(1)}%)</Badge>;
    } else if (porcentaje < 30) {
      return <Badge variant="secondary">Medio ({porcentaje.toFixed(1)}%)</Badge>;
    } else {
      return <Badge variant="default">Alto ({porcentaje.toFixed(1)}%)</Badge>;
    }
  };

  const calcularResumen = () => {
    const totalProductos = filteredProductos.length;
    const margenPromedio = filteredProductos.length > 0
      ? filteredProductos.reduce((sum, p) => sum + p.margen_porcentaje, 0) / filteredProductos.length
      : 0;
    const valorInventarioTotal = filteredProductos.reduce((sum, p) => sum + p.valor_inventario, 0);
    const productosMargenBajo = filteredProductos.filter(p => p.margen_porcentaje < 10).length;

    return { totalProductos, margenPromedio, valorInventarioTotal, productosMargenBajo };
  };

  const resumen = calcularResumen();

  // Top 10 productos por margen para gráfico
  const topProductos = [...filteredProductos]
    .sort((a, b) => b.margen_porcentaje - a.margen_porcentaje)
    .slice(0, 10)
    .map(p => ({
      nombre: p.codigo,
      margen: parseFloat(p.margen_porcentaje.toFixed(1)),
    }));

  const getBarColor = (margen: number) => {
    if (margen < 10) return "#ef4444";
    if (margen < 30) return "#f59e0b";
    return "#10b981";
  };

  return (
    <Layout>
      <div className="space-y-8">
        <PageHeader
          eyebrow="Finanzas"
          title="Análisis de"
          titleAccent="rentabilidad."
          lead="Margen por producto — compra vs. venta."
        />

        {/* StatCards */}
        <div className={`grid gap-4 ${isMobile ? 'grid-cols-2' : 'md:grid-cols-4'}`}>
          <StatCard
            label="Productos analizados"
            value={resumen.totalProductos}
            meta="Con precios definidos"
          />
          <StatCard
            label="Margen promedio"
            value={`${resumen.margenPromedio.toFixed(1)}%`}
            meta="Sobre precio de compra"
            trend={resumen.margenPromedio >= 20 ? "up" : "down"}
            trendValue={`${resumen.margenPromedio.toFixed(1)}%`}
          />
          <StatCard
            label="Valor de inventario"
            value={`$${resumen.valorInventarioTotal.toLocaleString()}`}
            meta="A precio de compra"
          />
          <StatCard
            label="Margen bajo"
            value={resumen.productosMargenBajo}
            meta="Productos con <10% margen"
            icon={<TrendingDown className="w-4 h-4" />}
          />
        </div>

        {/* Gráfico Top 10 */}
        {topProductos.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Top 10 Productos por Margen</CardTitle>
              <CardDescription>Productos con mayor porcentaje de utilidad</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topProductos} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="nombre" type="category" width={80} style={{ fontSize: '11px' }} />
                  <Tooltip formatter={(value: number) => `${value}%`} />
                  <Bar dataKey="margen" name="Margen %">
                    {topProductos.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getBarColor(entry.margen)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Filtros */}
        <Card>
          <CardHeader>
            <CardTitle>Detalle por Producto</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`flex gap-4 mb-4 ${isMobile ? 'flex-col' : 'flex-wrap'}`}>
              <div className={`relative ${isMobile ? 'w-full' : 'flex-1 min-w-[250px]'}`}>
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar producto..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className={isMobile ? 'w-full' : 'w-[200px]'}>
                <Select value={ordenamiento} onValueChange={setOrdenamiento}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="margen_desc">Mayor margen %</SelectItem>
                    <SelectItem value="margen_asc">Menor margen %</SelectItem>
                    <SelectItem value="venta_desc">Mayor precio venta</SelectItem>
                    <SelectItem value="valor_inv_desc">Mayor valor inventario</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              Mostrando{" "}
              <span className={searchTerm ? "text-primary font-medium" : ""}>
                {filteredProductos.length}
              </span>{" "}
              de {productos.length} productos
            </p>

            {/* Vista Mobile o Tabla */}
            {isMobile ? (
              <div className="space-y-3">
                {loading ? (
                  <p className="text-center py-8 text-muted-foreground">Cargando...</p>
                ) : filteredProductos.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No hay productos con precios definidos</p>
                ) : (
                  filteredProductos.map((producto) => (
                    <RentabilidadCardMobile key={producto.id} producto={producto} />
                  ))
                )}
              </div>
            ) : (
            /* Tabla Desktop */
            <div className="rounded-xl border border-ink-100 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-[10px] uppercase tracking-[0.16em] text-ink-500 font-medium">Código</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.16em] text-ink-500 font-medium">Producto</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.16em] text-ink-500 font-medium">Marca</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.16em] text-ink-500 font-medium text-right">Precio Compra</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.16em] text-ink-500 font-medium text-right">Precio Venta</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.16em] text-ink-500 font-medium text-right">Margen $</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.16em] text-ink-500 font-medium">Margen %</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.16em] text-ink-500 font-medium text-right">Stock</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.16em] text-ink-500 font-medium text-right">Valor Inv.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12">
                        <AlmasaLoading size={48} text="Cargando rentabilidad..." />
                      </TableCell>
                    </TableRow>
                  ) : filteredProductos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center">
                        No hay productos con precios definidos
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredProductos.map((producto) => (
                      <TableRow key={producto.id}>
                        <TableCell className="font-medium">{producto.codigo}</TableCell>
                        <TableCell>{producto.nombre}</TableCell>
                        <TableCell>{producto.marca || "—"}</TableCell>
                        <TableCell className="text-right font-sans tabular-nums lining-nums text-ink-700">
                          ${producto.precio_compra.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-sans tabular-nums lining-nums text-ink-700">
                          ${producto.precio_venta.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-sans font-semibold tabular-nums lining-nums text-emerald-600">
                          ${producto.margen_pesos.toFixed(2)}
                        </TableCell>
                        <TableCell>{getMargenBadge(producto.margen_porcentaje)}</TableCell>
                        <TableCell className="text-right">{producto.stock_actual}</TableCell>
                        <TableCell className="text-right">
                          ${producto.valor_inventario.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Rentabilidad;
